import { AfterViewInit, Component, effect, input, output, signal } from '@angular/core';
import * as L from 'leaflet';
import { MessageService } from 'primeng/api';
import { type Coordinate } from '../../shared/models/geolocation.model';
import { Pet } from '../../shared/models/pet.model';
import { LocationService } from './../../services/location/location.service';

@Component({
  selector: 'app-world-map',
  imports: [],
  templateUrl: './world-map.component.html',
  styleUrl: './world-map.component.scss'
})
export class WorldMapComponent implements AfterViewInit {
  readonly pet = input<Pet | null>(null);

  readonly zoomSteps = input<number>(2);
  readonly isZoomable = input<boolean>(true);
  readonly isScrollable = input<boolean>(true);
  readonly zoomRange = input<[number, number]>([6, 18]);
  readonly isDraggable = input<boolean>(true);
  readonly enableInfinityMap = input<boolean>(false);

  readonly styleClass = input<string>('');
  readonly initialCoord = input<Coordinate | null>(null);

  readonly fetchedCoords = output<Coordinate>();

  readonly isMapReady = signal<boolean>(false);

  private map: L.Map | undefined;
  private readonly defaultCoords: [number, number] = [-15.793889, -47.882778]; // Brasília, DF, Brazil
  private readonly defaultIcon = L.icon({
    iconUrl: '/assets/marker/pawn-marker.png',
    shadowUrl: '/assets/marker/marker-shadow.png',
    iconSize: [40, 40],
    shadowSize: [42, 42],
    iconAnchor: [0, 0],
    popupAnchor: [20, -5]
  });

  constructor(private readonly locationService: LocationService,
    private readonly messageService: MessageService) {

    effect(() => {
      if (this.isMapReady()) {
        const coord = this.initialCoord();

        if (coord) {
          this.populateMap(coord);
        } else {
          this.locationService.getCurrentLocation().subscribe({
            next: (position: Coordinate) => {
              this.populateMap(position);
              this.fetchedCoords.emit(position);
            },
            error: () => {
              this.map?.setView(new L.LatLng(...this.defaultCoords), this.zoomRange()[1]);
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não foi possível recuperar a localização do usuário!',
                life: 4000
              });
            }
          });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initMap();
    })
  }

  populateMap(coord: Coordinate): void {
    if (this.map) {
      const marker = L.marker([coord.latitude, coord.longitude], { icon: this.defaultIcon }).addTo(this.map);
      marker.bindPopup(this.buildPetMessage(this.pet()));

      this.map.setView(new L.LatLng(coord.latitude, coord.longitude), this.zoomRange()[1]);

      setTimeout(() => marker.openPopup(), 100);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível popular o mapa!',
        life: 4000
      });
      console.error('Map not initialized!');
    }
  }

  getTileOptions(): { [name: string]: L.Layer } {
    const currYear = new Date().getFullYear();

    const osmLayer = L.tileLayer(
      'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
      {
        minZoom: this.zoomRange()[0],
        maxZoom: this.zoomRange()[1],
        attribution: `&copy; ${currYear} <a href="http://osm.org/copyright">OpenStreetMap</a> contributors`
      }
    );

    return {
      'Open Street Map': osmLayer
    };
  }

  initMap(): void {
    L.Marker.prototype.options.icon = this.defaultIcon;

    const mapContainer = document.getElementById('map');
    if (!mapContainer) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Houve um erro durante a redenrização do mapa!',
        life: 4000
      });
      console.error('Map container not found! Make sure <div id="map"></div> exists in the DOM.');
      return;
    }

    const tileOptions = this.getTileOptions()
    const maxLongitude = this.enableInfinityMap() ? Infinity : 180;
    const maxBounds = L.latLngBounds(
      L.latLng(-90, -maxLongitude),
      L.latLng(90, maxLongitude)
    );

    this.map = L.map(mapContainer, {
      layers: [tileOptions['Open Street Map']],
      zoom: this.zoomRange()[0],
      zoomDelta: this.zoomSteps(),
      wheelPxPerZoomLevel: 60 / this.zoomSteps(),
      maxBounds: maxBounds,
      maxBoundsViscosity: .85,
      zoomControl: this.isZoomable(),
      scrollWheelZoom: this.isScrollable(),
      zoomAnimation: true,
      dragging: this.isDraggable(),
      worldCopyJump: true
    });

    this.isMapReady.set(true);

    L.control.scale({ metric: true, imperial: false }).addTo(this.map);
  }

  private buildPetMessage(pet: Pet | null = null): string {
    return pet ? `
    <div class="flex flex-col justify-center items-center w-full">
      <img src="${pet.files[0]}" class="rounded-md h-[100px] w-[100px] object-cover rounded-md"/>
      <p class="text-lg font-semibold">Oi eu sou ${pet.name}! 🐾</p>
    </div>
    ` : 'O pet foi visto aqui!';
  }
}
